/**
 * Product compare overlay — flex column grid with add-product modal search.
 */

const COMPARE_STORAGE_KEY = 'product-compare-items';
const COMPARE_MAX_DEFAULT = 4;
const COMPARE_SEARCH_DEBOUNCE_MS = 200;

class ProductCompare extends HTMLElement {
  constructor() {
    super();
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onOverlayClick = this._onOverlayClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onGridClick = this._onGridClick.bind(this);
    this._onModalSearchInput = this._onModalSearchInput.bind(this);
    this._onAddModalClick = this._onAddModalClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this.recommendations = [];
    this.recommendationsPromise = null;
    this.recommendationsLoadFailed = false;
    this.addingHandle = null;
    this.searchAbortController = null;
    this.searchDebounceTimer = null;
    this.addModalOpen = false;
    this.loading = false;
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);

    this.overlay = this.querySelector('[data-compare-overlay]');
    this.panel = this.querySelector('.product-compare__panel');
    this.closeButton = this.querySelector('[data-compare-close]');
    this.bodyContainer = this.querySelector('[data-compare-body]');

    this.addModal = this.querySelector('[data-compare-add-modal]');
    this.addModalPanel = this.querySelector('.product-compare__add-modal-panel');
    this.modalSearchInput = this.querySelector('[data-compare-modal-search]');
    this.modalSearchClear = this.querySelector('[data-compare-modal-search-clear]');
    this.modalResultsList = this.querySelector('[data-compare-modal-results]');
    this.modalStatus = this.querySelector('[data-compare-modal-status]');
    this.modalEmpty = this.querySelector('[data-compare-modal-empty]');

    this.maxProducts = parseInt(this.dataset.maxProducts, 10) || COMPARE_MAX_DEFAULT;
    this.baseProductId = this.dataset.baseProductId || null;
    this.baseHandle = this.dataset.baseHandle || null;
    this.baseProductType = this.dataset.baseProductType || null;
    this.sectionId = this.dataset.sectionId || 'product-compare-product-column';
    this.gridSectionId = this.dataset.gridSectionId || 'product-compare-grid';
    this.recommendationsUrl = this.dataset.recommendationsUrl || '';
    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl || window.routes?.predictive_search_url || '';

    this._refreshGridRefs();

    this.overlay?.addEventListener('click', this._onOverlayClick);
    this.closeButton?.addEventListener('click', this._onCloseClick);
    this.addEventListener('click', this._onGridClick);
    this.addModal?.addEventListener('click', this._onAddModalClick);
    this.modalSearchInput?.addEventListener('input', this._onModalSearchInput);
    this.modalSearchClear?.addEventListener('click', () => this._clearModalSearch());
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keyup', this._onKeyUp);

    this.removeAttribute('hidden');

    const params = new URLSearchParams(window.location.search);
    const compareHandle = params.get('compare');
    if (compareHandle) {
      requestAnimationFrame(() => this.openWithProduct(compareHandle));
    }
  }

  disconnectedCallback() {
    this.overlay?.removeEventListener('click', this._onOverlayClick);
    this.closeButton?.removeEventListener('click', this._onCloseClick);
    this.removeEventListener('click', this._onGridClick);
    this.addModal?.removeEventListener('click', this._onAddModalClick);
    this.modalSearchInput?.removeEventListener('input', this._onModalSearchInput);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keyup', this._onKeyUp);
    this._abortSearch();
    clearTimeout(this.searchDebounceTimer);
  }

  _refreshGridRefs() {
    this.grid = this.querySelector('[data-compare-grid]');
    this.payloadsContainer = this.querySelector('[data-compare-payloads]');
    this.addColumn = this.querySelector('[data-compare-add-column]');
    this.addOpenButton = this.querySelector('[data-compare-add-open]');
  }

  _setBaseProductFromDom() {
    const headerCell = this.querySelector('[data-compare-header-cell]');
    const productId = headerCell?.dataset.productId;
    const payloadEl = productId
      ? this.payloadsContainer?.querySelector(
          `[data-compare-column-payload][data-product-id="${productId}"]`
        )
      : null;

    this.baseProductId = productId || null;
    this.baseHandle = headerCell?.dataset.productHandle || null;
    this.baseProductType = null;

    if (payloadEl) {
      try {
        const payload = JSON.parse(payloadEl.textContent);
        this.baseProductType = payload.profile_type || payload.type || null;
      } catch (e) {
        /* ignore */
      }
    }
  }

  async openWithProduct(handle) {
    if (!handle || this.loading) return;

    this.loading = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}?sections=${encodeURIComponent(this.gridSectionId)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Compare grid request failed (${response.status})`);

      const data = await response.json();
      const html = data[this.gridSectionId];
      if (!html?.trim()) throw new Error('Empty compare grid response');

      if (this.bodyContainer) {
        this.bodyContainer.innerHTML = html;
      }

      this._refreshGridRefs();
      this._setBaseProductFromDom();
      this.recommendations = [];
      this.recommendationsPromise = null;
      this.recommendationsLoadFailed = false;
      this._updateLayout();
      this._persistStore();
      this._preloadRecommendations();
      this.open();
    } catch (error) {
      console.error('Compare: failed to open', error);
    } finally {
      this.loading = false;
    }
  }

  _onDocumentClick(event) {
    const trigger = event.target.closest('[data-compare-open]');
    if (!trigger) return;

    event.preventDefault();
    this.openWithProduct(trigger.dataset.compareOpen);
  }

  open() {
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this._preloadRecommendations();
    const focusTarget = this.closeButton || this.panel;
    if (typeof trapFocus === 'function' && this.panel) {
      trapFocus(this.panel, focusTarget);
    } else {
      focusTarget?.focus();
    }
  }

  close() {
    this._closeAddModal();
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.closeButton);
    }
  }

  _openAddModal() {
    if (!this.addModal || this.addModalOpen) return;

    this.addModalOpen = true;
    this.addModal.hidden = false;
    this.addModal.classList.add('is-open');

    if (this.modalSearchInput) {
      this.modalSearchInput.value = '';
    }
    this._toggleSearchClear();

    const showSuggestions = () => {
      this._setModalStatus(false);
      if (this.recommendationsLoadFailed) {
        this._renderModalResults([], { emptyMessage: 'Unable to load suggestions right now.' });
        return;
      }
      this._renderModalResults(this.recommendations);
    };

    if (this.recommendations.length) {
      showSuggestions();
    } else {
      this._setModalStatus(true);
      this._preloadRecommendations().then(showSuggestions).catch(showSuggestions);
    }

    const focusTarget = this.modalSearchInput || this.addModalPanel;
    if (typeof trapFocus === 'function' && this.addModalPanel) {
      trapFocus(this.addModalPanel, focusTarget);
    } else {
      focusTarget?.focus();
    }
  }

  _closeAddModal() {
    if (!this.addModal || !this.addModalOpen) return;

    this.addModalOpen = false;
    this.addModal.hidden = true;
    this.addModal.classList.remove('is-open');
    this._abortSearch();
    clearTimeout(this.searchDebounceTimer);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.addOpenButton);
    }

    this.addOpenButton?.focus();
  }

  _onAddModalClick(event) {
    if (event.target.closest('[data-compare-add-modal-close]')) {
      event.preventDefault();
      this._closeAddModal();
      return;
    }

    const result = event.target.closest('[data-compare-result]');
    if (result) {
      event.preventDefault();
      this._addProduct(result.dataset.productHandle, result.dataset.productType || null);
    }
  }

  _getProductColumns() {
    return [...this.querySelectorAll('[data-compare-header-cell]')];
  }

  _readColumnsFromDom() {
    return this._getProductColumns().map((headerCell) => {
      const productId = headerCell.dataset.productId;
      const payloadEl = this.payloadsContainer?.querySelector(
        `[data-compare-column-payload][data-product-id="${productId}"]`
      );

      if (payloadEl) {
        try {
          return JSON.parse(payloadEl.textContent);
        } catch (e) {
          /* fall through */
        }
      }

      return {
        id: Number(productId),
        handle: headerCell.dataset.productHandle,
      };
    });
  }

  _getComparedIds() {
    return new Set(this._readColumnsFromDom().map((item) => Number(item.id)));
  }

  _getComparedHandles() {
    return new Set(this._readColumnsFromDom().map((item) => item.handle));
  }

  _persistStore() {
    const items = this._readColumnsFromDom();
    const anchor = items[0];
    if (!anchor) return;

    try {
      localStorage.setItem(
        COMPARE_STORAGE_KEY,
        JSON.stringify({
          anchorId: anchor.id,
          anchorHandle: anchor.handle,
          profileType: anchor.profile_type || anchor.type,
          items,
        })
      );
    } catch (e) {
      /* ignore */
    }
  }

  _updateLayout() {
    const productCount = this._getProductColumns().length;
    const showAddColumn = productCount < this.maxProducts;

    if (this.addColumn) {
      this.addColumn.hidden = !showAddColumn;
    }

    if (!showAddColumn) {
      this._closeAddModal();
    }

    if (this.grid) {
      this.grid.style.setProperty('--compare-product-columns', productCount);
    }
  }

  _preloadRecommendations() {
    if (this.recommendationsPromise) return this.recommendationsPromise;
    if (!this.baseProductId || !this.recommendationsUrl) {
      this.recommendationsPromise = Promise.resolve();
      return this.recommendationsPromise;
    }
    if (this.recommendations.length) {
      this.recommendationsPromise = Promise.resolve();
      return this.recommendationsPromise;
    }

    this.recommendationsPromise = (async () => {
      try {
        const url = `${this.recommendationsUrl}.json?product_id=${this.baseProductId}&intent=related&limit=10`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);

        const data = await response.json();
        this.recommendations = data.products || [];
        this.recommendationsLoadFailed = false;
      } catch (error) {
        console.error('Compare: failed to load recommendations', error);
        this.recommendations = [];
        this.recommendationsLoadFailed = true;
        throw error;
      }
    })();

    return this.recommendationsPromise;
  }

  _filterProducts(products) {
    const comparedIds = this._getComparedIds();
    const comparedHandles = this._getComparedHandles();

    return products.filter((product) => {
      if (this.baseProductType && product.type && product.type !== this.baseProductType) {
        return false;
      }
      if (comparedIds.has(Number(product.id)) || comparedHandles.has(product.handle)) {
        return false;
      }
      return true;
    });
  }

  _normalizeProduct(product) {
    const image = product.featured_image || product.image || null;
    const imageUrl = typeof image === 'string' ? image : image?.url || image?.src || null;

    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      type: product.type || product.product_type || null,
      featured_image: imageUrl,
    };
  }

  _renderResultItem(product) {
    const image = product.featured_image
      ? `<img class="product-compare__result-image" src="${product.featured_image}" alt="" loading="lazy" width="48" height="48">`
      : '<span class="product-compare__result-image product-compare__result-image--placeholder" aria-hidden="true"></span>';

    const typeAttr = product.type ? ` data-product-type="${product.type}"` : '';

    return `
      <li class="product-compare__result" role="option">
        <button
          type="button"
          class="product-compare__result-button"
          data-compare-result
          data-product-handle="${product.handle}"
          data-product-id="${product.id}"${typeAttr}
        >
          ${image}
          <span class="product-compare__result-text">
            <span class="product-compare__result-title">${product.title}</span>
          </span>
        </button>
      </li>
    `;
  }

  _setModalStatus(isLoading) {
    if (!this.modalStatus) return;
    this.modalStatus.hidden = !isLoading;
  }

  _renderModalResults(products, { emptyMessage } = {}) {
    if (!this.modalResultsList) return;

    const list = (products || []).map((p) => this._normalizeProduct(p));
    const matches = this._filterProducts(list);

    this.modalResultsList.innerHTML = matches.map((product) => this._renderResultItem(product)).join('');

    if (this.modalEmpty) {
      const showEmpty = matches.length === 0;
      this.modalEmpty.hidden = !showEmpty;
      if (showEmpty) {
        const query = (this.modalSearchInput?.value || '').trim();
        this.modalEmpty.textContent =
          emptyMessage || (query ? 'No matching products found.' : 'No suggested products to compare.');
      }
    }
  }

  _abortSearch() {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
  }

  async _fetchSearchResults(query) {
    if (!this.predictiveSearchUrl) return [];

    this._abortSearch();
    this.searchAbortController = new AbortController();

    const url = `${this.predictiveSearchUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`;

    const response = await fetch(url, { signal: this.searchAbortController.signal });
    if (!response.ok) throw new Error(`Search request failed (${response.status})`);

    const data = await response.json();
    return data.resources?.results?.products || [];
  }

  _toggleSearchClear() {
    if (!this.modalSearchClear || !this.modalSearchInput) return;
    this.modalSearchClear.hidden = !this.modalSearchInput.value.trim();
  }

  _clearModalSearch() {
    if (!this.modalSearchInput) return;

    this.modalSearchInput.value = '';
    this._abortSearch();
    clearTimeout(this.searchDebounceTimer);
    this._toggleSearchClear();
    this._setModalStatus(false);
    this._renderModalResults(this.recommendations);
    this.modalSearchInput.focus();
  }

  _onModalSearchInput() {
    this._toggleSearchClear();
    clearTimeout(this.searchDebounceTimer);

    this.searchDebounceTimer = setTimeout(async () => {
      const query = (this.modalSearchInput?.value || '').trim();

      if (!query) {
        this._setModalStatus(false);
        this._renderModalResults(this.recommendations);
        return;
      }

      this._setModalStatus(true);

      try {
        const products = await this._fetchSearchResults(query);
        this._setModalStatus(false);
        this._renderModalResults(products);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Compare: search failed', error);
        this._setModalStatus(false);
        this._renderModalResults([], { emptyMessage: 'Unable to search right now.' });
      }
    }, COMPARE_SEARCH_DEBOUNCE_MS);
  }

  _mountProductFragment(html) {
    const payloadMatch = html.match(
      /<script[^>]*data-compare-column-payload[\s\S]*?<\/script>/i
    );
    const tableHtml = html.replace(/<script[\s\S]*?<\/script>/gi, '').trim();
    if (!tableHtml) return;

    const doc = new DOMParser().parseFromString(
      `<table><tbody>${tableHtml}</tbody></table>`,
      'text/html'
    );

    const headerCell = doc.querySelector('[data-compare-header-cell]');
    const headerRow = this.querySelector('[data-compare-header-row]');
    if (headerCell && headerRow) {
      headerRow.appendChild(headerCell);
    }

    doc.querySelectorAll('[data-compare-value]').forEach((cell) => {
      const fieldRow = this.querySelector(
        `[data-compare-field-row="${cell.dataset.fieldKey}"]`
      );
      if (fieldRow) {
        fieldRow.appendChild(cell);
      }
    });

    const fillCell = doc.querySelector('[data-compare-fill-cell]');
    const fillRow = this.querySelector('[data-compare-fill-row]');
    if (fillCell && fillRow) {
      fillRow.appendChild(fillCell);
    }

    if (payloadMatch && this.payloadsContainer) {
      const payloadWrapper = document.createElement('div');
      payloadWrapper.innerHTML = payloadMatch[0];
      const payload = payloadWrapper.querySelector('[data-compare-column-payload]');
      if (payload) {
        this.payloadsContainer.appendChild(payload);
      }
    }
  }

  _insertProductColumn(html) {
    if (!html) return;
    this._mountProductFragment(html);
  }

  _removeProduct(productId) {
    this.querySelectorAll(`[data-product-id="${productId}"]`).forEach((el) => {
      if (el.closest('[data-compare-payloads]')) return;
      el.remove();
    });

    this.payloadsContainer
      ?.querySelector(`[data-compare-column-payload][data-product-id="${productId}"]`)
      ?.remove();

    this._persistStore();
    this._updateLayout();
  }

  async _addProduct(handle, productType) {
    if (!handle || this.addingHandle) return;

    const productCount = this._getProductColumns().length;
    if (productCount >= this.maxProducts) return;
    if (this._getComparedHandles().has(handle)) return;

    if (this.baseProductType && productType && productType !== this.baseProductType) {
      return;
    }

    this.addingHandle = handle;
    this.addColumn?.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}?sections=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Section request failed (${response.status})`);

      const data = await response.json();
      const html = data[this.sectionId];
      if (!html?.trim()) throw new Error('Empty compare column response');

      this._insertProductColumn(html);
      this._persistStore();
      this._updateLayout();
      this._closeAddModal();
    } catch (error) {
      console.error('Compare: failed to add product', error);
    } finally {
      this.addingHandle = null;
      this.addColumn?.removeAttribute('aria-busy');
    }
  }

  _onGridClick(event) {
    if (event.target.closest('[data-compare-add-open]')) {
      event.preventDefault();
      this._openAddModal();
      return;
    }

    const removeButton = event.target.closest('[data-compare-remove]');
    if (removeButton) {
      event.preventDefault();
      const headerCell = removeButton.closest('[data-compare-header-cell]');
      if (headerCell?.dataset.productId) {
        this._removeProduct(headerCell.dataset.productId);
      }
    }
  }

  _onKeyUp(event) {
    if (event.code !== 'Escape' || !this.classList.contains('is-open')) return;

    if (this.addModalOpen) {
      this._closeAddModal();
      return;
    }

    this.close();
  }

  _onOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  _onCloseClick() {
    this.close();
  }
}

if (!customElements.get('product-compare')) {
  customElements.define('product-compare', ProductCompare);
}
